import AppContext from '@/AppContext';
import { EntityList } from '@/components/EntityList';
import { ListColumn } from '@/components/ListLayout';
import { getFontAwesomeIconForFileExtension } from '@/lib/utils';
import { Form, Grid, Label, Pagination } from '@/semantic-ui/components';
import { useStores } from '@/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useCallback, useContext, useState } from 'react';
import { useSearchParams } from 'react-router';
import { OnboardingDocument, toNum } from 'sol-data';
import PositionFilterDropdown from '../PositionFilterDropdown';

const RESULTS_PER_PAGE = 10;
const DEBOUNCE_DELAY = 200;

const deleteWarning = 'Are you sure you want to delete this Onboarding Document?';

export const OnboardingDocumentTemplatesList = observer(() => {
  const { onboardingDocumentStore } = useStores();
  const [currentPage, setCurrentPage] = useState(1);
  const [onboardingDocuments, setOnboardingDocuments] = useState<OnboardingDocument[]>([]);
  const appContext = useContext(AppContext);
  const isAdmin = appContext?.isAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page') ? Number(searchParams.get('page')) : 1;
  const positionId = searchParams.get('position')
    ? Number(searchParams.get('position'))
    : undefined;
  const [search, setSearch] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const fetchData = async (
    pageNumber: number,
    searchValue?: string,
    positionIdValue?: number,
    includeDeletedValue?: boolean,
  ) => {
    onboardingDocumentStore.clearCurrent();

    const data = await onboardingDocumentStore.fetchAllPaginated({
      page: pageNumber,
      search: searchValue,
      positionId: positionIdValue,
      includeDeleted: includeDeletedValue,
    });

    setCurrentPage(pageNumber);
    setOnboardingDocuments(data);
  };

  const { isLoading, errorMessage } = onboardingDocumentStore;

  const columns: ListColumn[] = [
    {
      name: 'Title',
      key: 'title',
      width: { sm: 3, lg: 3 },
      render: ({ title }) => <span>{title}</span>,
    },
    {
      name: 'Created By',
      key: 'postedBy',
      width: { sm: 2, lg: 2 },
      textAlign: 'center',
      render: ({ postedBy }) => (
        <div
          style={{
            textAlign: 'center',
          }}
        >
          {postedBy}
        </div>
      ),
    },
    {
      name: 'Posted On',
      key: 'postedOn',
      width: { sm: 2, lg: 2 },
      textAlign: 'center',
      render: ({ postedOn }) => (
        <div
          style={{
            textAlign: 'center',
          }}
        >
          {postedOn}
        </div>
      ),
    },
    {
      name: 'Positions',
      key: 'positions',
      width: { sm: 3, lg: 3 },
      textAlign: 'center',
      render: ({ positions }) => {
        return (
          <div
            style={{
              textAlign: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '4px',
            }}
          >
            {positions.map(({ label, id }: { label: string; id: number }) => (
              <Label key={id} size="tiny" color="blue">
                {label.toUpperCase()}
              </Label>
            ))}
          </div>
        );
      },
    },
    {
      name: 'Require Signature',
      key: 'requireSignature',
      width: { sm: 1, lg: 1 },
      textAlign: 'center',
      render: ({ requireSignature }) => (
        <div style={{ textAlign: 'center' }}>{requireSignature ? 'Yes' : 'No'}</div>
      ),
    },
    {
      name: 'File Name',
      key: 'fileName',
      width: { sm: 3, lg: 3 },
      render: ({ fileName }) => (
        <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
          <FontAwesomeIcon
            icon={['far', getFontAwesomeIconForFileExtension(fileName.split('.').pop()!)]}
            className="reveal-icon"
            style={{ marginRight: 4 }}
          />
          {fileName}
        </div>
      ),
    },
  ];

  const handleRemove = async (id: number) => {
    await onboardingDocumentStore.delete(id);
    setOnboardingDocuments(onboardingDocuments.filter((document) => document.id !== id));
  };

  const handlePositionChange = async (value: number) => {
    setSearch('');

    await changePage(1, '', value);
  };

  const changePage = async (
    pageNumber: number,
    searchValue?: string,
    positionIdValue?: number,
    includeDeletedValue?: boolean,
  ) => {
    onboardingDocumentStore.setPage(pageNumber);

    setSearchParams({
      page: pageNumber.toString(),
      position: positionIdValue?.toString() ?? '',
    });

    await fetchData(pageNumber, searchValue, positionIdValue, includeDeletedValue);
  };

  const updateSearch = useCallback(
    debounce(async (searchValue: string) => {
      await changePage(1, searchValue, undefined);
    }, DEBOUNCE_DELAY),
    [page, positionId],
  );

  const handleIncludeDeletedChange = async (checked: boolean) => {
    setIncludeDeleted(checked);
    await changePage(page, search, positionId, checked);
  };

  return (
    <>
      <EntityList
        title="Onboarding Documents"
        searchLabel="Search by document or file name"
        columns={columns}
        items={onboardingDocuments}
        newLink="/onboarding/documents/templates/add"
        titleProp="title"
        onRemove={({ id }) => handleRemove(id)}
        dialog={deleteWarning}
        searchValue={search}
        onSearchChange={(filter) => {
          setSearch(filter);
          updateSearch(filter);
        }}
        onDismiss={() => onboardingDocumentStore.clearError()}
        onRefresh={() => changePage(page, search, positionId)}
        errorMessage={errorMessage}
        isLoading={isLoading}
        link={
          isAdmin
            ? ({ id, deleteDatetime }) =>
                deleteDatetime ? '#' : `/onboarding/documents/templates/${id}`
            : undefined
        }
        noNewButton={!isAdmin}
        searchHeaderContent={
          <Form
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 10,
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Form.Field style={{ margin: 0 }}>
              <Form.Checkbox
                label="Show archived documents"
                checked={includeDeleted}
                onChange={(_, { checked }) => handleIncludeDeletedChange(checked || false)}
              />
            </Form.Field>
            <Form.Field>
              <PositionFilterDropdown
                limited
                clearable
                name="position"
                placeholder="Filter by position"
                positionId={positionId ? toNum(positionId) : undefined}
                onChange={(_, value) => handlePositionChange(value)}
              />
            </Form.Field>
          </Form>
        }
      />
      <Grid centered>
        <Grid.Row>
          <Pagination
            activePage={currentPage}
            totalPages={Math.ceil(onboardingDocumentStore.allDocumentsCount / RESULTS_PER_PAGE)}
            onPageChange={(_, { activePage }) => {
              changePage(activePage as number, search, positionId);
            }}
            siblingRange={2}
            boundaryRange={0}
            firstItem={{ content: 'First' }}
            lastItem={{ content: 'Last' }}
            ellipsisItem={null}
            prevItem={null}
            nextItem={null}
          />
        </Grid.Row>
      </Grid>
    </>
  );
});
